import { EntrancePlantDetailDTO, EntrancePlantStructureCreateDTO, IPeBuildingConfig, PeInterfaceConfig, IPeRiserConfig, RiserInterfaceMode, PeBuildingConfig, BuildingCreateDTO, PlantConfigStep } from '@/../client-generator/generated-clients/plants';
import { Vue, Options } from 'vue-class-component';
import plantsClient from '@/core/services/plants';
import { SET_SELECTED_PLANT_ID } from '@/core/store/action-types';
import { getInterfaceModeLabel } from '@/core/utils/EntrancePlant/entrance-plant.utils';

interface RiserWithInterfaces extends IPeRiserConfig {
  interfaces: PeInterfaceConfig[];
  isBackbone?: boolean;
}

interface BuildingConfig extends IPeBuildingConfig {
  linkedRisersConfig: RiserWithInterfaces[];
  linkedRisersIds: string[];
}

@Options({
  name: 'DialogEntrancePlantCreateSetupBuilding',
  props: {
    plant: {
      required: true,
      type: Object as () => EntrancePlantDetailDTO,
    },
    structureCreate: {
      required: true,
      type: Object as () => EntrancePlantStructureCreateDTO,
    },
    plantId: {
      type: Number,
      required: false,
    },   
    isEdit: {
      type: Boolean,
      default: false
    }
  },
  components: {},
  emits: ['is-loading', 'onBack', 'onNext', 'plant-edited'],
})
export default class DialogEntrancePlantCreateSetupBuilding extends Vue {
  plant!: EntrancePlantDetailDTO;
  plantId!: number;
  structureCreate!: EntrancePlantStructureCreateDTO;

  currentBuildingIndex: number = 0;

  backboneAndRisersWithInterfaces: RiserWithInterfaces[] = [];
  buildingConfigs: BuildingConfig[] = [];
  
  getInterfaceModeLabel = getInterfaceModeLabel;

  get pageTitle(): string {
    return `${this.$t('AppResourceEP.SetupBuildingWizard')} ${this.currentBuildingIndex + 1}`;
  }

  get totalBuildings(): number {
    return this.buildingConfigs.length;
  }

  get currentBuilding(): BuildingConfig | undefined {
    return this.buildingConfigs[this.currentBuildingIndex];
  }

  get isLastBuilding(): boolean {
    return this.currentBuildingIndex >= this.totalBuildings - 1;
  }

  get nextButtonLabel(): string {
    return this.isLastBuilding ? this.$t('AppResource.Finish') as string : this.$t('AppResource.Continue') as string;
  }

  get riserCount(): number {
    const risersCount = this.structureCreate.pePlantConfig?.pePlantConfig?.risers?.length || 0;
    const hasBackbone = this.hasBackboneApartments;
    return risersCount + (hasBackbone ? 1 : 0);
  }

  get hasBackboneApartments(): boolean {
    return this.backboneApartmentsCount > 0;
  }

  get backboneApartmentsCount(): number {
    return this.structureCreate.pePlantConfig?.numbersOfBackboneApartments ?? 0;
  }

  get maxBuildingsCount(): number {
    return this.riserCount;
  }

  get availableRiserOptions(): { riserId: string; riserName: string, isBackbone: boolean, interfacesComboMode: string }[] {
    return this.backboneAndRisersWithInterfaces.map(riser => {
      let interfacesComboMode = '';
      if (riser.interfaces.length == 1) {
        this.$t('AppResourceEP.ShortComboMode5IndependentRiser');
      } else if (riser.interfaces.length == 2) {
        interfacesComboMode = this.$t('AppResourceEP.ShortComboMode6ExtendedRiserAndOneMode7ExtendedRiserExpansion');
      } else if (riser.interfaces.length > 2) {
        interfacesComboMode = this.$t('AppResourceEP.ShortComboMode6ExtendedRiserAndTwoMode7ExtendedRiserExpansion');
      }
      return {
        riserId: riser.id || '',
        riserName: riser.name || '',
        isBackbone: riser.isBackbone ?? false,
        interfacesComboMode: riser.isBackbone ? '' : interfacesComboMode,
      };
    });
  }

  get isNextDisabled(): boolean {
    const building = this.currentBuilding;
    if (!building) return true;
    return Object.keys(building.linkedRisersConfig || {}).length === 0;
  }

  mounted(): void {
    this.initializeRiserWithInterfaces();
    this.initializeBuildingConfigs();
  }

  initializeRiserWithInterfaces(): void {
    const interfaces = this.structureCreate.pePlantConfig?.pePlantConfig?.interfaces || [];
    this.backboneAndRisersWithInterfaces = (this.structureCreate.pePlantConfig?.pePlantConfig?.risers || []).map(riser => {
      const linkedInterfaces = interfaces.filter(intf => intf.peRiserConfigId === riser.id);
      return {
        ...riser,
        interfaces: linkedInterfaces,
      };
    });

    if (this.hasBackboneApartments) {
      const backboneInterface = interfaces.find(intf => intf.isBackboneInterface);
      if (backboneInterface) {
        this.backboneAndRisersWithInterfaces.unshift({
          index: -1,
          id: backboneInterface.peRiserConfigId,
          name: backboneInterface.name,
          numberOfApartments: backboneInterface.numberOfApartments,
          interfaces:[backboneInterface],
          isBackbone: true,
        });
      }
    }
  }

  initializeBuildingConfigs(): void {
    if (this.structureCreate.pePlantConfig?.pePlantConfig?.buildings && this.structureCreate.pePlantConfig.pePlantConfig.buildings.length > 0) {
      this.buildingConfigs = this.structureCreate.pePlantConfig.pePlantConfig.buildings.map(building => {
        const linkedRisersIds = this.structureCreate.pePlantConfig?.pePlantConfig?.risers?.filter(riser => {
          return building.id === riser.peBuildingConfigId;
        }).map(riser => riser.id!) || [];
        const linkedRisersConfig = this.backboneAndRisersWithInterfaces.filter(riser => linkedRisersIds.includes(riser.id!));
        return {
          ...building,
          linkedRisersConfig: linkedRisersConfig,
          linkedRisersIds: linkedRisersIds,
        } as BuildingConfig;
      });
    }
    this.currentBuildingIndex = 0;
  }

  isRiserDisabledForBuilding(buildingIndex: number, riserId: string): boolean {
    const isLinkedToOtherBuilding = this.buildingConfigs.some((building, index) => 
      index !== buildingIndex && building.linkedRisersConfig.some(riser => riser.id === riserId)
    );
    return isLinkedToOtherBuilding;
  }

  toggleRiserLink(buildingIndex: number, riserId: string): void {
    const building = this.buildingConfigs[buildingIndex];
    if (!building) return;

    if (!building.linkedRisersConfig) {
      building.linkedRisersConfig = [];
    }

    // Only sync linkedRisersConfig based on linkedRisersIds (which is updated by v-model)
    const isNowLinked = building.linkedRisersIds.includes(riserId);
    const wasLinked = building.linkedRisersConfig.some(riser => riser.id === riserId);

    if (isNowLinked && !wasLinked) {
      const riser = this.backboneAndRisersWithInterfaces.find(r => r.id === riserId);
      if (riser) {
        building.linkedRisersConfig.push(riser);
        building.linkedRisersConfig.sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
      }
    } else if (!isNowLinked && wasLinked) {
      building.linkedRisersConfig = building.linkedRisersConfig.filter(riser => riser.id !== riserId);
    }
  }

  handleBackButton(): void {
    if (this.currentBuildingIndex > 0) {
      this.currentBuildingIndex--;
    } else {
      this.$emit('onBack', 'DialogEntrancePlantCreateSetupAddBuildings');
    }
  }

  async handleNextButton() {
    this.$emit('is-loading', true);
    await this.updatePlantStructure(this.getDataToSave());

    if (this.isLastBuilding) {
      this.structureCreate.pePlantConfig!.currentStep = PlantConfigStep.END_CONFIG;
      await this.updatePlantStructure(new EntrancePlantStructureCreateDTO({
        pePlantConfig: this.structureCreate.pePlantConfig,
        riserFlow: true,
      }));
      this.$emit('is-loading', false);
      
      this.goToPlantDetail();
    } else {
      this.$emit('is-loading', false);
      this.currentBuildingIndex++;
    }
  }

  goToPlantDetail() {
    this.$nextTick(async () => {
      await this.$store.dispatch(SET_SELECTED_PLANT_ID, this.plantId);
      this.$router.push({
        name: 'page-entranceplant-dashboard',
        params: { plantId: this.plantId.toString() ?? '' },
      });
      this.$toast.add({
        severity: 'success',
        summary: this.$t('AppResource.PlantCreated'),
        closable: false,
        life: 3000,
      });
      const summary = this.$t('general.synced');
      setTimeout(() => {
        this.$toast.add({
          severity: 'success',
          summary: summary,
          closable: false,
          life: 3000,
        });
      }, 1500);
    });
  }

  getDataToSave(): EntrancePlantStructureCreateDTO {
    this.buildingConfigs.sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
    const currentBuilding = this.buildingConfigs[this.currentBuildingIndex];
    const buildingConfigsToSave: BuildingCreateDTO[] = [
      new BuildingCreateDTO({
        buildingId: currentBuilding.id,
        name: currentBuilding.name,
        apartmentsCount: currentBuilding.linkedRisersConfig.reduce((sum, riser) => sum + (riser.numberOfApartments || 0), 0),
        linkedRisersIds: currentBuilding.linkedRisersIds,
      })
    ];

    this.structureCreate.pePlantConfig!.currentStep = PlantConfigStep.BUILDINGS_RISERS_LINK;
    return new EntrancePlantStructureCreateDTO({
      buildingsCreate: buildingConfigsToSave,
      pePlantConfig: this.structureCreate.pePlantConfig,
      riserFlow: true,
    });
  }

  private async updatePlantStructure(structure: EntrancePlantStructureCreateDTO): Promise<void> {
    if (!this.plant) return;

    try {
      const result = await plantsClient.structure(this.plant.desktopId!, structure);
      this.structureCreate.pePlantConfig = result.pePlantConfig;
      this.structureCreate.buildingsCreate = [...(this.structureCreate.buildingsCreate || []), ...(result.buildingsCreate || [])];
    } catch {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 6000,
      });
      this.$emit('is-loading', false);
    }
  }
}
